<template>
  <PreviewFrame :accent="slide.accent">
    <template #presenter>
      <div class="pa-question">
        <span class="pa-bar pa-bar--xl"></span>
        <span class="pa-bar pa-bar--md"></span>
      </div>
      <div class="pa-results">
        <div
          v-for="(opt, i) in options"
          :key="opt.letter"
          class="pa-row"
          :class="{ '--correct': opt.correct }"
        >
          <span class="pa-letter">{{ opt.letter }}</span>
          <div class="pa-track">
            <div
              class="pa-fill"
              :style="{ '--row-pct': opt.pct + '%', '--row-i': i }"
            ></div>
          </div>
          <span class="pa-count">{{ opt.count }}</span>
          <span v-if="opt.correct" class="material-symbols-rounded pa-check">check</span>
        </div>
      </div>
    </template>

    <template #audience>
      <span class="pa-phone-q"></span>
      <button
        v-for="(opt, i) in options"
        :key="opt.letter"
        type="button"
        class="pa-phone-option"
        :class="{ '--tapped': i === tapIndex }"
        :style="{ '--row-i': i }"
        tabindex="-1"
      >
        <span class="pa-phone-letter">{{ opt.letter }}</span>
        <span class="pa-bar pa-bar--md"></span>
        <span v-if="i === tapIndex" class="material-symbols-rounded pa-phone-check">check</span>
      </button>
    </template>
  </PreviewFrame>
</template>

<script lang="ts">
import Vue, { PropType } from 'vue'
import type { SlideType } from '../../data/slideTypeCatalog'
import PreviewFrame from './PreviewFrame.vue'

type Option = { letter: string; count: number; pct: number; correct: boolean }

export default Vue.extend({
  name: 'PreviewPickAnswer',
  components: { PreviewFrame },
  props: {
    slide: { type: Object as PropType<SlideType>, required: true },
  },
  data() {
    return { tapIndex: 1 }
  },
  computed: {
    options(): Option[] {
      const counts = [4, 14, 2, 6]
      const total = counts.reduce((a, b) => a + b, 0)
      return ['A', 'B', 'C', 'D'].map((letter, i) => ({
        letter,
        count: counts[i],
        pct: Math.round((counts[i] / total) * 100),
        correct: i === this.tapIndex,
      }))
    },
  },
})
</script>

<style scoped lang="scss">
.pa-bar {
  display: block; height: 8px; border-radius: 999px;
  background: rgba(15, 15, 20, 0.1);
}
.pa-bar--xl { width: 70%; }
.pa-bar--md { width: 40%; }

.pa-question {
  display: flex; flex-direction: column; gap: 6px;
  padding-bottom: 8px;
  border-bottom: 1px dashed color-mix(in srgb, var(--accent) 20%, transparent);
}
.pa-question .pa-bar:first-child { background: rgba(15, 15, 20, 0.2); }

.pa-results { display: grid; gap: 7px; }
.pa-row {
  display: grid;
  grid-template-columns: 18px 1fr auto auto;
  align-items: center;
  gap: 8px;
}
.pa-letter {
  width: 18px; height: 18px;
  border-radius: 999px;
  background: rgba(15, 15, 20, 0.06);
  color: rgba(15, 15, 20, 0.55);
  font-size: 10px; font-weight: 700;
  display: grid; place-items: center;
}
.pa-row.--correct .pa-letter { background: var(--accent); color: white; }
.pa-track {
  height: 10px; border-radius: 999px;
  background: rgba(15, 15, 20, 0.06);
  overflow: hidden;
}
.pa-fill {
  height: 100%; width: 0;
  border-radius: 999px;
  background: var(--accent);
  /* Synced cycle: rows fill in waves so the live update is obvious. */
  animation: pa-grow 4s var(--ease-out, cubic-bezier(0.23, 1, 0.32, 1)) infinite;
  animation-delay: calc(var(--row-i, 0) * 80ms + 800ms);
}
.pa-row.--correct .pa-fill { animation-name: pa-grow-correct; }
.pa-count {
  font-size: 10px; font-weight: 700;
  color: rgba(15, 15, 20, 0.55);
  font-variant-numeric: tabular-nums;
  min-width: 22px; text-align: right;
}
.pa-check { font-size: 14px; color: var(--accent); }

/* ---- Audience phone ---- */
.pa-phone-q {
  display: block;
  height: 8px; border-radius: 999px;
  width: 70%;
  background: rgba(15, 15, 20, 0.18);
  margin-bottom: 4px;
}
.pa-phone-option {
  appearance: none; background: white;
  position: relative;
  display: grid;
  grid-template-columns: 18px 1fr auto;
  align-items: center;
  gap: 8px;
  padding: 7px 9px;
  border-radius: 8px;
  border: 1px solid rgba(15, 15, 20, 0.08);
  cursor: pointer;
  font-family: inherit;
  text-align: left;
}
.pa-phone-letter {
  width: 18px; height: 18px;
  border-radius: 999px;
  background: rgba(15, 15, 20, 0.06);
  color: rgba(15, 15, 20, 0.55);
  font-size: 10px; font-weight: 700;
  display: grid; place-items: center;
}
.pa-phone-option.--tapped {
  border-color: color-mix(in srgb, var(--accent) 60%, transparent);
  background: color-mix(in srgb, var(--accent) 10%, white);
  /* Tap precedes the bar fill — see animation-delay alignment. */
  animation: pa-tap 4s var(--ease-out, cubic-bezier(0.23, 1, 0.32, 1)) infinite;
}
.pa-phone-option.--tapped .pa-phone-letter { background: var(--accent); color: white; }
.pa-phone-check { font-size: 14px; color: var(--accent); }

@keyframes pa-grow {
  0%, 12% { width: 0; }
  60%     { width: var(--row-pct, 50%); }
  92%, 100% { width: var(--row-pct, 50%); }
}
@keyframes pa-grow-correct {
  0%, 12% { width: 0; }
  55%     { width: 78%; }
  92%, 100% { width: 78%; }
}
@keyframes pa-tap {
  0%, 4%   { box-shadow: 0 0 0 0 color-mix(in srgb, var(--accent) 35%, transparent); }
  18%      { box-shadow: 0 0 0 8px color-mix(in srgb, var(--accent) 0%, transparent); }
  100%     { box-shadow: 0 0 0 0 color-mix(in srgb, var(--accent) 0%, transparent); }
}

@media (prefers-reduced-motion: reduce) {
  .pa-fill { animation: none; width: var(--row-pct, 50%); }
  .pa-row.--correct .pa-fill { width: 78%; }
  .pa-phone-option.--tapped { animation: none; }
}
</style>
